import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { axios } from '@/services/axios';
import { downloadCSV, extractUrlParams } from '@/utils/utils';
import { CONFIG } from './constants';

/* handles downloading Calling Rates' CSV template*/
export default function useCSVTemplate() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [jsonData, setJSONData] = useState([]);
    const [template, setTemplate] = useState(null);
    const { pathname: locationPath } = useLocation();
    const isCallingRates = locationPath.includes('calling-rates');
    const getConfig = (isCallingRates) =>
        isCallingRates ? CONFIG.callingRates : CONFIG.numberBlockRates;
    const config = getConfig(isCallingRates);

    const { child } = useSelector((state) => {
        return { ...state.table };
    });
    const { pathname } = useSelector((state) => state.url);
    const { plan, planID } = extractUrlParams(pathname);

    /**
     * transforms JSON Data from BE fields into more human readable CSV heading columns
     * @param {*} data
     * @returns
     */
    const transformJSONData = (data, config) => {
        const { fields, requiredFields, rateFields } = config;
        const allKeys = Object.keys(fields);
        if (!data?.length) {
            const emptyRow = allKeys.reduce((obj, key) => {
                if (requiredFields.includes(key)) obj[fields[key]] = '';
                else if (rateFields.includes(key))
                    obj[fields[key]] = '0.000000';
                else obj[fields[key]] = 0;
                return obj;
            }, {});
            return [emptyRow];
        }
        return data.map((row) => {
            return allKeys.reduce((acc, key) => {
                const value = row[key];
                const fieldName = fields[key];
                acc[fieldName] = value;
                if (key === 'isActive' || key === 'bill') {
                    acc[fieldName] = value === true ? '1' : '0';
                }
                return acc;
            }, {});
        });
    };

    /**
     * handles downloading calling rates data from BE.
     */
    const downloadRatesData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/${config.endpoint}/${planID}`);
            const newJSONData = transformJSONData(response.data, config);
            setJSONData(newJSONData);
            if (newJSONData.length === 1) {
                return;
            }
        } catch (error) {
            setError(error);
            window.alert(
                `Could not download ${isCallingRates ? 'calling rates' : 'number block rates'} data at this time. Please try again later.`,
            );
        } finally {
            setLoading(false);
        }
    };

    /**
     * handle download template UI button
     */
    const handleDownloadTemplate = () => {
        downloadRatesData();
    };

    useEffect(() => {
        /**
         * converts the template from JSON into CSV
         */
        const processTemplate = () => {
            const res = Papa.unparse(jsonData);
            setTemplate(res);
            const fileName = child
                ? `${config.fileName} - ${child?.name}.csv`
                : config.templateFilename;
            downloadCSV(res, fileName);
        };
        if (jsonData?.length) {
            processTemplate();
        }
    }, [jsonData, child, config.templateFilename, config.fileName]);

    return {
        handleDownloadTemplate,
        loading,
        error,
        template,
        planName: plan,
        planID,
    };
}
